import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../utils/ToastSettings";
import { ChatToolsService } from "../../services/Chats/ChatToolsService";
import { ChatToolModel } from "../../models/Chat/ChatTool/ChatToolModel";

interface ContextProps {
  tool: ChatToolModel | null;
  setTool: Dispatch<SetStateAction<ChatToolModel | null>>;
  tools: ChatToolModel[];
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: ChatToolModel) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleReactivate: (id: number) => Promise<boolean>;
}

export const ChatToolsContext = createContext<ContextProps>({} as ContextProps);

interface ChatToolsProviderProps {
  children: ReactNode;
}

export const ChatToolsProvider = ({ children }: ChatToolsProviderProps) => {
  const [tool, setTool] = useState<ChatToolModel | null>(null);
  const [tools, setTools] = useState<ChatToolModel[]>([]);
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const service = new ChatToolsService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/chats/tools/list")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setTools(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setTools([]);
      setPages({});
      setError("Não foi possível carregar as ferramentas.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: ChatToolModel) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar a ferramenta");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar a ferramenta"
      );
      return false;
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.reactivate(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao reativar a ferramenta"
      );
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <ChatToolsContext.Provider
      value={{
        tool,
        setTool,
        tools,
        params,
        setParams,
        pages,
        setPages,
        isLoading,
        setIsLoading,
        error,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleReactivate,
      }}
    >
      {children}
    </ChatToolsContext.Provider>
  );
};

export const useChatTools = () => useContext(ChatToolsContext);